/**
 * Permission catalog — the only place to add a backend flag or map it
 * to a page. Keep keys identical to PATCH /team-leaders/:id.
 *
 * 1. Add the flag to PERMISSION_FLAGS / TEAM_LEADER_PERMISSION_DEFAULTS
 * 2. Map it under NAV_ITEM_PERMISSIONS, COMPANY_TAB_PERMISSIONS, or
 *    ROUTE_PERMISSIONS (any listed flag unlocks that surface)
 * 3. Add an alias in PERMISSION_ALIASES if the API ever sends another name
 */

export const PERMISSION_FLAGS = [
  'salary',
  'payroll',
  'transactions',
  'employees',
  'employees_edit',
  'employees_create',
  'employees_delete',
  'employees_approve',
  'checkin',
  'attendance',
  'daily_attendance',
  'departments',
  'departments_edit',
  'branches',
  'branches_edit',
  'tasks',
  'tasks_create',
  'tasks_edit',
  'tasks_delete',
  'leave_requests',
  'leave_approve',
  'permission_requests',
  'permission_approve',
  'reports',
  'reports_salary',
  'reports_attendance',
  'categories',
  'categories_edit',
  'equipment',
  'equipment_edit',
  'tools',
  'tools_edit',
  'positions',
  'positions_edit',
  'notifications',
  'dashboard',
  'devices',
];

/** Defaults used when an admin assigns team-leader permissions. */
export const TEAM_LEADER_PERMISSION_DEFAULTS = {
  salary: false,
  payroll: false,
  transactions: false,
  employees: true,
  employees_edit: false,
  employees_create: false,
  employees_delete: false,
  employees_approve: false,
  checkin: true,
  attendance: true,
  daily_attendance: true,
  departments: true,
  departments_edit: false,
  branches: false,
  branches_edit: false,
  tasks: true,
  tasks_create: true,
  tasks_edit: true,
  tasks_delete: false,
  leave_requests: true,
  leave_approve: true,
  permission_requests: true,
  permission_approve: true,
  reports: true,
  reports_salary: false,
  reports_attendance: true,
  categories: false,
  categories_edit: false,
  equipment: false,
  equipment_edit: false,
  tools: false,
  tools_edit: false,
  positions: false,
  positions_edit: false,
  notifications: true,
  dashboard: false,
  devices: false,
};

/**
 * Action flags that only make sense after the parent view flag is on.
 * Edit / create / delete / approve switches stay hidden until the view is granted.
 */
export const PERMISSION_DEPENDENTS = {
  employees: ['employees_edit', 'employees_create', 'employees_delete', 'employees_approve'],
  departments: ['departments_edit'],
  branches: ['branches_edit'],
  tasks: ['tasks_create', 'tasks_edit', 'tasks_delete'],
  leave_requests: ['leave_approve'],
  permission_requests: ['permission_approve'],
  categories: ['categories_edit'],
  equipment: ['equipment_edit'],
  tools: ['tools_edit'],
  positions: ['positions_edit'],
};

/**
 * Alternate names the API may send. Values must be a catalog flag.
 * Matching is case-insensitive after normalizeAliasKey().
 */
export const PERMISSION_ALIASES = {
  view_employees: 'employees',
  employees_view: 'employees',
  viewemployees: 'employees',
  dailyattendance: 'daily_attendance',
  leaverequests: 'leave_requests',
  employeesapprove: 'employees_approve',
  leaveapprove: 'leave_approve',
  permissionrequests: 'permission_requests',
  permissionapprove: 'permission_approve',
  reportssalary: 'reports_salary',
  reportsattendance: 'reports_attendance',
  view_reports: 'reports',
  viewreports: 'reports',
  view_tasks: 'tasks',
  viewtasks: 'tasks',
  view_departments: 'departments',
  viewdepartments: 'departments',
  view_branches: 'branches',
  viewbranches: 'branches',
  check_in: 'checkin',
  payrolls: 'payroll',
  payslips: 'payroll',
};

export const ADMIN_NAV_ITEMS = [
  'dashboard',
  'employees',
  'payroll',
  'holidays',
  'permissions',
  'company',
  'reports',
  'attendance',
  'tasks',
  'chat',
  'documents',
];

export const EMPLOYEE_NAV_ITEMS = [
  'dashboard',
  'holidays',
  'permissions',
  'salary',
  'chat',
  'tasks',
  'documents',
];

/** Always visible to team leaders (self-service, same as an employee). */
export const TEAM_LEADER_SELF_NAV_ITEMS = [
  'dashboard',
  'holidays',
  'salary',
  'chat',
  'tasks',
  'documents',
];

/** @deprecated use TEAM_LEADER_SELF_NAV_ITEMS */
export const TEAM_LEADER_BASE_NAV_ITEMS = TEAM_LEADER_SELF_NAV_ITEMS;

/**
 * Backend flags that unlock a sidebar item. Any one flag is enough.
 * Items in TEAM_LEADER_SELF_NAV_ITEMS do not need a flag.
 */
export const NAV_ITEM_PERMISSIONS = {
  employees: ['employees', 'employees_edit', 'employees_create', 'employees_delete', 'employees_approve'],
  // Company payroll (/payroll) APIs require `salary`. Self-service payslips
  // stay on /salary and do not use this item.
  payroll: ['salary'],
  company: [
    'departments',
    'departments_edit',
    'branches',
    'branches_edit',
    'equipment',
    'equipment_edit',
    'tools',
    'tools_edit',
    'positions',
    'positions_edit',
    'devices',
    'categories',
    'categories_edit',
    'transactions',
  ],
  reports: ['reports', 'reports_salary', 'reports_attendance'],
  attendance: ['attendance', 'daily_attendance', 'checkin'],
  permissions: ['permission_requests', 'permission_approve'],
  holidays: ['leave_requests', 'leave_approve'],
  notifications: ['notifications'],
  dashboard: ['dashboard'],
};

export const COMPANY_TAB_PERMISSIONS = {
  branches: ['branches', 'branches_edit'],
  departments: ['departments', 'departments_edit'],
  documents: ['employees', 'departments', 'branches'],
  assets: ['equipment', 'equipment_edit'],
  tools: ['tools', 'tools_edit'],
  'job-positions': ['positions', 'positions_edit'],
  'leave-requests': ['leave_requests', 'leave_approve'],
  devices: ['devices'],
  admins: null,
  orders: ['transactions'],
  'document-approvals': ['employees', 'employees_edit'],
  transfers: ['employees', 'employees_edit'],
};

/**
 * Per-report flags matching backend requirePermission on /reports/*.
 * Keep IDs in sync with ReportsPage.jsx.
 */
export const REPORT_ITEM_PERMISSIONS = {
  1: ['reports'],
  2: ['salary'],
  3: ['reports'],
  4: ['leave_requests'],
  5: ['leave_requests'],
  6: ['employees'],
  7: ['employees'],
  8: ['employees'],
  9: ['employees'],
  10: ['salary'],
  11: ['equipment'],
  12: ['reports_attendance'],
  13: ['reports'],
  14: ['leave_requests'],
};

/** `null` = admin exclusive. */
export const ROUTE_PERMISSIONS = {
  '/employees': NAV_ITEM_PERMISSIONS.employees,
  '/payroll': NAV_ITEM_PERMISSIONS.payroll,
  '/reports': NAV_ITEM_PERMISSIONS.reports,
  '/company': NAV_ITEM_PERMISSIONS.company,
  '/notifications': NAV_ITEM_PERMISSIONS.notifications,
  '/attendance': NAV_ITEM_PERMISSIONS.attendance,
  '/permissions': NAV_ITEM_PERMISSIONS.permissions,
  '/settings': null,
  '/system-logs': null,
};

export const ALL_NAV_ITEM_IDS = [
  ...new Set([
    ...ADMIN_NAV_ITEMS,
    ...EMPLOYEE_NAV_ITEMS,
    ...TEAM_LEADER_SELF_NAV_ITEMS,
    'overview',
    'settings',
  ]),
];

export const NESTED_ACTION_ALIASES = {
  view: '',
  read: '',
  show: '',
  edit: '_edit',
  update: '_edit',
  create: '_create',
  add: '_create',
  delete: '_delete',
  remove: '_delete',
  approve: '_approve',
};

export const MASKED_PLACEHOLDER = '****';

/**
 * view / create / edit / delete / approve flags per resource.
 * Add a resource here when a new page needs action-level checks.
 */
export const RESOURCE_ACTIONS = {
  employees: {
    view: ['employees', 'employees_edit', 'employees_create', 'employees_delete', 'employees_approve'],
    create: ['employees_create'],
    edit: ['employees_edit'],
    delete: ['employees_delete'],
    approve: ['employees_approve'],
  },
  salary: {
    view: ['salary', 'payroll'],
    edit: ['salary', 'payroll'],
  },
  payroll: {
    view: ['salary'],
    edit: ['salary'],
    delete: ['salary'],
  },
  tasks: {
    view: ['tasks', 'tasks_create', 'tasks_edit', 'tasks_delete'],
    create: ['tasks_create'],
    edit: ['tasks_edit'],
    delete: ['tasks_delete'],
  },
  departments: {
    view: ['departments', 'departments_edit'],
    create: ['departments_edit'],
    edit: ['departments_edit'],
    delete: ['departments_edit'],
  },
  branches: {
    view: ['branches', 'branches_edit'],
    create: ['branches_edit'],
    edit: ['branches_edit'],
    delete: ['branches_edit'],
  },
  positions: {
    view: ['positions', 'positions_edit'],
    create: ['positions_edit'],
    edit: ['positions_edit'],
    delete: ['positions_edit'],
  },
  equipment: {
    view: ['equipment', 'equipment_edit'],
    create: ['equipment_edit'],
    edit: ['equipment_edit'],
    delete: ['equipment_edit'],
  },
  tools: {
    view: ['tools', 'tools_edit'],
    create: ['tools_edit'],
    edit: ['tools_edit'],
    delete: ['tools_edit'],
  },
  categories: {
    view: ['categories', 'categories_edit'],
    create: ['categories_edit'],
    edit: ['categories_edit'],
    delete: ['categories_edit'],
  },
  devices: {
    view: ['devices'],
    edit: ['devices'],
    delete: ['devices'],
  },
  leave_requests: {
    view: ['leave_requests', 'leave_approve'],
    approve: ['leave_approve'],
  },
  permission_requests: {
    view: ['permission_requests', 'permission_approve'],
    approve: ['permission_approve'],
  },
  reports: {
    view: ['reports', 'reports_salary', 'reports_attendance'],
  },
  reports_salary: {
    view: ['reports_salary', 'salary', 'payroll'],
  },
  reports_attendance: {
    view: ['reports_attendance', 'attendance', 'daily_attendance'],
  },
  transactions: {
    view: ['transactions'],
  },
  documents: {
    view: ['employees', 'employees_edit'],
    edit: ['employees_edit'],
    delete: ['employees_edit'],
  },
  attendance: {
    view: ['attendance', 'daily_attendance', 'checkin'],
  },
  daily_attendance: {
    view: ['daily_attendance', 'attendance'],
  },
};

/**
 * Sensitive fields. Unlisted fields stay visible once the page itself is allowed.
 * Map a new field here to mask it with stars for team leaders without the flag.
 */
export const FIELD_PERMISSIONS = {
  'employee.salary': ['salary', 'payroll'],
  'employee.baseSalary': ['salary', 'payroll'],
  'employee.nationalId': ['employees_edit'],
  'employee.bankAccount': ['salary', 'payroll'],
  'payroll.amount': ['salary', 'payroll'],
  'payroll.netSalary': ['salary', 'payroll'],
  'reports.salary': ['reports_salary', 'salary', 'payroll'],
};
